export const MAP_CENTER = { lat: 10.460741167512973, lng: -66.88793489835992 };
export const MAP_ZOOM = 12;

export const CITIES = [
  { name: "Caracas", ...MAP_CENTER },
  { name: "Maracaibo", lat: 10.6427, lng: -71.6125 },
  { name: "Lechería", lat: 10.1806, lng: -64.6872 },
  { name: "Barquisimeto", lat: 10.0678, lng: -69.3474 },
  { name: "Valencia", lat: 10.1621, lng: -68.0077 },
  { name: "Maracay", lat: 10.2442, lng: -67.5958 },
  { name: "La Guaira", lat: 10.599, lng: -66.9346 },
];

const USELESS_ROAD_NAMES = [
  "unnamed road",
  "unnamed",
  "sin nombre",
  "calle sin nombre",
];

const SKIP_FALLBACK_RE =
  /\b(venezuela|distrito capital|miranda|carabobo|zulia|lara|anzo[aá]tegui|caracas|maracaibo|valencia|maracay|barquisimeto|lecher[ií]a|la guaira)\b/i;

const HIGHWAY_NAME_RE =
  /\b(autopista|autov[ií]a|carretera|distribuidor|tramo)\b/i;
const AVENUE_NAME_RE = /\b(avenida|\bav\.?)\b/i;
const MINOR_STREET_RE = /^(calle|callej[oó]n|vereda|pasaje|transversal|carrera)\b/i;

const getComponent = (components, type) =>
  components?.find((c) => c.types?.includes(type))?.long_name;

const isUsefulPlaceName = (name) => {
  if (!name) return false;
  const normalized = name.trim().toLowerCase();
  if (USELESS_ROAD_NAMES.includes(normalized)) return false;
  if (SKIP_FALLBACK_RE.test(normalized) && normalized.split(" ").length <= 3) {
    // evita ciudad/estado/país sueltos
    if (
      /^(venezuela|distrito capital|caracas|maracaibo|valencia|maracay|barquisimeto)$/i.test(
        normalized
      )
    ) {
      return false;
    }
  }
  return true;
};

const scoreRouteName = (name) => {
  if (!isUsefulPlaceName(name)) return -1;
  if (HIGHWAY_NAME_RE.test(name)) return 100;
  if (AVENUE_NAME_RE.test(name)) return 80;
  if (MINOR_STREET_RE.test(name)) return 20;
  return 50;
};

const EARTH_RADIUS_M = 6371000;
export const PROXIMITY_GROUP_THRESHOLD_M = 500;
const HEAT_ZONE_PADDING_M = 450;
const HEAT_ZONE_MIN_RADIUS_M = 350;

export const CLUSTER_COLORS = [
  "#2563eb",
  "#e11d48",
  "#16a34a",
  "#d97706",
  "#7c3aed",
  "#0891b2",
  "#db2777",
  "#65a30d",
  "#ea580c",
  "#4f46e5",
];

export const hexToRgba = (hex, alpha) => {
  const normalized = String(hex || "").replace("#", "");
  if (normalized.length !== 6) return `rgba(0, 0, 0, ${alpha})`;
  const n = parseInt(normalized, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const distanceSq = (lat1, lng1, lat2, lng2) => {
  const dLat = lat1 - lat2;
  const dLng = lng1 - lng2;
  return dLat * dLat + dLng * dLng;
};

const toRad = (deg) => (deg * Math.PI) / 180;

/** Distancia haversine en metros. */
export const distanceMeters = (lat1, lng1, lat2, lng2) => {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
};

const latLngToWorldPixel = (lat, lng, zoom) => {
  const scale = 256 * 2 ** zoom;
  const x = ((lng + 180) / 360) * scale;
  const sinY = Math.min(
    Math.max(Math.sin((lat * Math.PI) / 180), -0.9999),
    0.9999
  );
  const y =
    (0.5 - Math.log((1 + sinY) / (1 - sinY)) / (4 * Math.PI)) * scale;
  return { x, y };
};

const estimateLabelSize = (name) => ({
  w: Math.min(160, Math.max(48, 16 + String(name || "").length * 7.2)),
  h: 22,
});

const boxesOverlap = (a, b, pad = 6) =>
  !(
    a.x + a.w + pad <= b.x ||
    b.x + b.w + pad <= a.x ||
    a.y + a.h + pad <= b.y ||
    b.y + b.h + pad <= a.y
  );

const labelOffsetCandidates = () => {
  const offsets = [{ x: 0, y: 20 }];
  for (let ring = 1; ring <= 10; ring++) {
    const count = 6 + ring * 2;
    const radius = 16 + ring * 24;
    for (let k = 0; k < count; k++) {
      const angle = (2 * Math.PI * k) / count - Math.PI / 2;
      offsets.push({
        x: Math.round(radius * Math.cos(angle)),
        y: Math.round(radius * Math.sin(angle)),
      });
    }
  }
  return offsets;
};

/**
 * Offsets en px (relativos al marker) para que las etiquetas no se tapen.
 * items: { i, lat, lng, name }
 */
export const getNonOverlappingLabelOffsets = (items, zoom = MAP_ZOOM) => {
  const candidates = labelOffsetCandidates();
  const placed = [];
  const offsets = {};
  const z = Number.isFinite(zoom) ? zoom : MAP_ZOOM;

  for (const item of items || []) {
    if (!Number.isFinite(item?.lat) || !Number.isFinite(item?.lng)) continue;

    const marker = latLngToWorldPixel(item.lat, item.lng, z);
    const { w, h } = estimateLabelSize(item.name);
    let chosen = candidates[0];
    let placedBox = false;

    for (const candidate of candidates) {
      const box = {
        x: marker.x + candidate.x - w / 2,
        y: marker.y + candidate.y,
        w,
        h,
      };
      if (!placed.some((prev) => boxesOverlap(box, prev))) {
        chosen = candidate;
        placed.push(box);
        placedBox = true;
        break;
      }
    }

    if (!placedBox) {
      placed.push({
        x: marker.x + chosen.x - w / 2,
        y: marker.y + chosen.y,
        w,
        h,
      });
    }

    offsets[item.i] = chosen;
  }

  return offsets;
};

/**
 * Agrupa items { d, i } en clusters transitivos: si A↔B y B↔C ≤ umbral, A-B-C van juntos.
 * Conserva el orden de primera aparición (útil tras sortDevicesByDistanceToPoint).
 * Solo coords válidas se unen; inválidas quedan como grupos individuales.
 */
export const groupDevicesByProximity = (
  items,
  thresholdMeters = PROXIMITY_GROUP_THRESHOLD_M
) => {
  const list = items || [];
  const n = list.length;
  if (n === 0) return [];

  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i) => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  for (let i = 0; i < n; i++) {
    const a = list[i]?.d;
    if (!Number.isFinite(a?.lat) || !Number.isFinite(a?.lng)) continue;
    for (let j = i + 1; j < n; j++) {
      const b = list[j]?.d;
      if (!Number.isFinite(b?.lat) || !Number.isFinite(b?.lng)) continue;
      if (distanceMeters(a.lat, a.lng, b.lat, b.lng) <= thresholdMeters) {
        union(i, j);
      }
    }
  }

  const groupMap = new Map();
  const order = [];
  list.forEach((item, idx) => {
    const root = find(idx);
    if (!groupMap.has(root)) {
      groupMap.set(root, []);
      order.push(root);
    }
    groupMap.get(root).push(item);
  });

  return order.map((root) => groupMap.get(root));
};

/** Círculo que cubre a los miembros de un cluster, con padding mínimo visible. */
export const getClusterHeatZone = (members) => {
  const points = (members || [])
    .map((item) => item?.d)
    .filter((d) => Number.isFinite(d?.lat) && Number.isFinite(d?.lng));

  if (points.length < 2) return null;

  const lat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  const lng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;

  let maxDist = 0;
  for (const p of points) {
    maxDist = Math.max(maxDist, distanceMeters(lat, lng, p.lat, p.lng));
  }

  return {
    center: { lat, lng },
    radius: Math.max(HEAT_ZONE_MIN_RADIUS_M, maxDist + HEAT_ZONE_PADDING_M),
  };
};

/**
 * Ordena items { d, i } por distancia al punto (lat, lng), más cercano primero.
 */
export const sortDevicesByDistanceToPoint = (items, lat, lng) => {
  const valid = [];
  const invalid = [];

  for (const item of items || []) {
    if (Number.isFinite(item?.d?.lat) && Number.isFinite(item?.d?.lng)) {
      valid.push(item);
    } else {
      invalid.push(item);
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || valid.length <= 1) {
    return [...valid, ...invalid];
  }

  valid.sort((a, b) => {
    const da = distanceSq(lat, lng, a.d.lat, a.d.lng);
    const db = distanceSq(lat, lng, b.d.lat, b.d.lng);
    return da - db;
  });

  return [...valid, ...invalid];
};

/**
 * Ordena items { d, i } agrupando vecinos (nearest-neighbor).
 * Empieza por el más cercano a (startLat, startLng).
 */
export const sortDevicesByProximity = (items, startLat, startLng) => {
  const remaining = [];
  const invalid = [];

  for (const item of items || []) {
    if (Number.isFinite(item?.d?.lat) && Number.isFinite(item?.d?.lng)) {
      remaining.push(item);
    } else {
      invalid.push(item);
    }
  }

  if (remaining.length <= 1) {
    return [...remaining, ...invalid];
  }

  let startIndex = 0;
  if (Number.isFinite(startLat) && Number.isFinite(startLng)) {
    let bestDist = Number.POSITIVE_INFINITY;
    remaining.forEach((item, idx) => {
      const dist = distanceSq(startLat, startLng, item.d.lat, item.d.lng);
      if (dist < bestDist) {
        bestDist = dist;
        startIndex = idx;
      }
    });
  }

  const ordered = [];
  const pool = [...remaining];
  let current = pool.splice(startIndex, 1)[0];
  ordered.push(current);

  while (pool.length) {
    let bestIdx = 0;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < pool.length; i++) {
      const dist = distanceSq(
        current.d.lat,
        current.d.lng,
        pool[i].d.lat,
        pool[i].d.lng
      );
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    current = pool.splice(bestIdx, 1)[0];
    ordered.push(current);
  }

  return [...ordered, ...invalid];
};

const getResultLatLng = (result) => {
  const loc = result?.geometry?.location;
  if (!loc) return null;
  return {
    lat: typeof loc.lat === "function" ? loc.lat() : loc.lat,
    lng: typeof loc.lng === "function" ? loc.lng() : loc.lng,
  };
};

const normalizeKey = (name) => name.trim().toLowerCase();

/**
 * Arma detalle de vías: autopista, avenida y/o calle + zona,
 * sin ciudad/estado/país.
 */
export const formatStreetAddress = (geocodeResponse, lat = null, lng = null) => {
  const results = geocodeResponse?.results || [];
  if (!results.length) return "Dirección no disponible";

  const routeCandidates = [];
  for (const result of results) {
    const route = getComponent(result.address_components, "route");
    const score = scoreRouteName(route);
    if (score < 0) continue;

    const point = getResultLatLng(result);
    const dist =
      Number.isFinite(lat) && Number.isFinite(lng) && point
        ? distanceSq(lat, lng, point.lat, point.lng)
        : Number.POSITIVE_INFINITY;

    routeCandidates.push({ route, score, dist, result });
  }

  routeCandidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.dist - b.dist;
  });

  // Elige hasta 3 vías distintas: autopista, avenida y calle
  const selectedRoutes = [];
  const seen = new Set();
  const pickOne = (predicate) => {
    const found = routeCandidates.find(
      (c) => predicate(c) && !seen.has(normalizeKey(c.route))
    );
    if (found) {
      seen.add(normalizeKey(found.route));
      selectedRoutes.push(found);
    }
  };

  pickOne((c) => HIGHWAY_NAME_RE.test(c.route));
  pickOne((c) => AVENUE_NAME_RE.test(c.route));
  pickOne((c) => MINOR_STREET_RE.test(c.route) || c.score < 80);

  // Si no hubo categorías claras, toma las mejores distintas
  if (!selectedRoutes.length) {
    for (const candidate of routeCandidates) {
      const key = normalizeKey(candidate.route);
      if (seen.has(key)) continue;
      seen.add(key);
      selectedRoutes.push(candidate);
      if (selectedRoutes.length >= 3) break;
    }
  }

  const parts = [];
  for (const selected of selectedRoutes) {
    const streetNumber = getComponent(
      selected.result.address_components,
      "street_number"
    );
    // Número solo en calles/transversales, no en autopistas
    if (streetNumber && selected.score < 80) {
      parts.push(`${streetNumber} ${selected.route}`);
    } else {
      parts.push(selected.route);
    }
  }

  const sourceResult = selectedRoutes[0]?.result || results[0];
  const components = sourceResult.address_components || [];

  const intersection = getComponent(components, "intersection");
  if (
    isUsefulPlaceName(intersection) &&
    !parts.some((p) => normalizeKey(p).includes(normalizeKey(intersection)))
  ) {
    parts.push(intersection);
  }

  let neighborhood =
    getComponent(components, "neighborhood") ||
    getComponent(components, "sublocality_level_1") ||
    getComponent(components, "sublocality");

  if (!isUsefulPlaceName(neighborhood)) {
    for (const result of results) {
      const candidate =
        getComponent(result.address_components, "neighborhood") ||
        getComponent(result.address_components, "sublocality_level_1") ||
        getComponent(result.address_components, "sublocality");
      if (isUsefulPlaceName(candidate)) {
        neighborhood = candidate;
        break;
      }
    }
  }

  if (
    isUsefulPlaceName(neighborhood) &&
    !parts.some((p) => normalizeKey(p) === normalizeKey(neighborhood))
  ) {
    parts.push(neighborhood);
  }

  const filteredParts = parts.filter(isUsefulPlaceName);
  if (filteredParts.length) {
    return filteredParts.join(" · ");
  }

  const fallbackParts = (sourceResult.formatted_address || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => isUsefulPlaceName(s) && !SKIP_FALLBACK_RE.test(s))
    .slice(0, 3);

  return fallbackParts.join(" · ") || "Ubicación sin calle cercana";
};
